import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function ElectricalRepairsPage() {
  useEffect(() => {
    document.title = 'Electrical Repairs Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert electrical repairs in Watford. NICEIC approved electricians for socket repairs, lighting fixes, wiring repairs & emergency callouts. Call 020 3198 1818 for same-day service.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Expert electrical repairs in Watford. NICEIC approved electricians for socket repairs, lighting fixes, wiring repairs & emergency callouts. Call 020 3198 1818 for same-day service.';
      document.head.appendChild(meta);
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "url": "https://beyondelectrical.co.uk/services/repairs/electrical-repairs",
      "telephone": "02031981818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Watford",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "WD17",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday"
        ],
        "opens": "00:00",
        "closes": "23:59"
      },
      "serviceType": "Electrical Repairs",
      "areaServed": {
        "@type": "City",
        "name": "Watford"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    { emoji: '🔌', title: 'Socket Repairs', description: 'Fixing loose, damaged, or non-working sockets throughout your property' },
    { emoji: '💡', title: 'Light Fixture Repairs', description: 'Repairing faulty lights, switches, and dimmer controls' },
    { emoji: '📦', title: 'Consumer Unit Issues', description: 'Fixing tripping breakers, faulty RCDs, and fuse box problems' },
    { emoji: '🔧', title: 'Wiring Repairs', description: 'Repairing damaged cables, loose connections, and faulty circuits' },
    { emoji: '⚡', title: 'Power Loss Issues', description: 'Diagnosing and fixing partial or complete power loss problems' },
    { emoji: '🚨', title: 'Emergency Repairs', description: 'Fast response for urgent electrical faults and safety hazards' }
  ];

  const signs = [
    { emoji: '💥', title: 'Sparking Outlets', description: 'Visible sparks when plugging in appliances' },
    { emoji: '🔥', title: 'Burning Smell', description: 'Unusual odors from sockets or switches' },
    { emoji: '⚠️', title: 'Frequent Tripping', description: 'Circuit breakers constantly switching off' },
    { emoji: '🌡️', title: 'Hot Switches', description: 'Switches or sockets warm to the touch' },
    { emoji: '💡', title: 'Flickering Lights', description: 'Lights dimming or flickering unexpectedly' },
    { emoji: '🔌', title: 'Dead Outlets', description: 'Sockets not providing power to devices' }
  ];

  const benefits = [
    { emoji: '⚡', title: 'Fast Response', description: 'Same-day service available for urgent repairs in Watford and surrounding areas' },
    { emoji: '🛡️', title: 'Safety First', description: 'All repairs meet BS 7671 wiring regulations with full certification' },
    { emoji: '💰', title: 'Fixed Pricing', description: 'Transparent quotes with no hidden costs - you know the price before we start' }
  ];

  const areas = [
    'Watford', 'Rickmansworth', 'Bushey', 'Abbots Langley', 'Croxley Green',
    'Chorleywood', 'Northwood', 'Pinner', 'Harrow', 'Radlett', 'Borehamwood', 'St Albans'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0A1628] via-[#0F1F3A] to-[#0A1628] text-white pt-32 pb-20">
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/145c78cf408145e283b6140303431f91.jpg')] bg-cover bg-center opacity-10"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Electrical Repairs in Watford
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              Fast, reliable electrical repair services from NICEIC approved electricians. Same-day service available for urgent repairs.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-10">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <i className="ri-shield-check-line text-[#D4AF37]"></i>
                <span className="text-sm whitespace-nowrap">NICEIC Approved</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <i className="ri-time-line text-[#D4AF37]"></i>
                <span className="text-sm whitespace-nowrap">Same-Day Service</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <i className="ri-map-pin-line text-[#D4AF37]"></i>
                <span className="text-sm whitespace-nowrap">Serving Watford</span>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:02031981818" className="bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1628] px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line"></i>
                Call 020 3198 1818
              </a>
              <a href="mailto:info@beyondelectrical.co.uk" className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-mail-line"></i>
                Get a Quote
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Our Electrical Repair Services
              </h2>
              <p className="text-xl text-gray-600">
                Comprehensive repair solutions for all your electrical problems
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                  <div className="w-16 h-16 flex items-center justify-center text-4xl mb-4">
                    {service.emoji}
                  </div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Signs You Need This Service */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Signs You Need Electrical Repairs
              </h2>
              <p className="text-xl text-gray-600">
                Don't ignore these warning signs - contact us immediately
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {signs.map((sign, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all duration-300">
                  <div className="w-16 h-16 flex items-center justify-center text-4xl mb-4">
                    {sign.emoji}
                  </div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-3">
                    {sign.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {sign.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Why Choose Our Repair Service
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg text-center">
                  <div className="w-20 h-20 flex items-center justify-center text-5xl mx-auto mb-6">
                    {benefit.emoji}
                  </div>
                  <h3 className="text-2xl font-bold text-[#0A1628] mb-4">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Beyond Electrical */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#0F1F3A] to-[#0A1628] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Why Choose Beyond Electrical
              </h2>
              <p className="text-xl text-gray-300">
                Trusted electrical repair experts in Watford
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-[#D4AF37] rounded-full mx-auto mb-4">
                  <i className="ri-time-line text-4xl text-[#0A1628]"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3">Same-Day Service</h3>
                <p className="text-gray-300">Fast response times for urgent electrical repairs</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-[#D4AF37] rounded-full mx-auto mb-4">
                  <i className="ri-shield-check-line text-4xl text-[#0A1628]"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3">NICEIC Certified</h3>
                <p className="text-gray-300">All work certified and compliant with regulations</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-[#D4AF37] rounded-full mx-auto mb-4">
                  <i className="ri-award-line text-4xl text-[#0A1628]"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3">15+ Years Experience</h3>
                <p className="text-gray-300">Trusted by hundreds of Watford homeowners</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">500+</div>
                <div className="text-gray-300">Repairs Completed</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">100%</div>
                <div className="text-gray-300">Satisfaction Rate</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">24/7</div>
                <div className="text-gray-300">Emergency Service</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">15+</div>
                <div className="text-gray-300">Years Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-8">
              Areas We Cover
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Providing electrical repair services throughout Watford and surrounding areas
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {areas.map((area, index) => (
                <span key={index} className="bg-gray-100 text-[#0A1628] px-6 py-3 rounded-full font-medium hover:bg-[#D4AF37] hover:text-white transition-all duration-300 cursor-pointer">
                  {area}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#0F1F3A] to-[#0A1628] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Need Electrical Repairs in Watford?
            </h2>
            <p className="text-xl text-gray-300 mb-10">
              Contact us today for fast, reliable electrical repair services. Same-day appointments available.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:02031981818" className="bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1628] px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line"></i>
                Call 020 3198 1818
              </a>
              <a href="mailto:info@beyondelectrical.co.uk" className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-mail-line"></i>
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
